import React, { useState, useEffect } from "react"
import {
  Breadcrumb,
  notification,
  Spin,
  Tabs,
  Tag,
  Card,
  Row,
  Col,
  Typography,
  Space,
  Descriptions,
} from "antd"
import MetaTags from "react-meta-tags"
import { ProjectFiles } from "./ProjectFiles"
import { ProjectPackages } from "./ProjectPackages"
import { ProjectUpdate } from "./ProjectUpdate"
import {
  addFileGasProject,
  addFileGasProjectSingle,
  resetGasProjectFlag,
  updateGasProject,
} from "store/actions"
import { getGasProjectsFullFilterApi } from "helpers/backend_helper"
import { connect } from "react-redux"
import { withTranslation } from "react-i18next"
import { useParams, Link } from "react-router-dom"
import {
  HomeOutlined,
  FileOutlined,
  ToolOutlined,
  UserOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  EditOutlined,
} from "@ant-design/icons"

const { Title, Text } = Typography

const GasProjectDashboardExe = props => {
  const {
    loading,
    onResetGasProjectFlag,
    triggerReload,
  } = props
  const [api, contextHolder] = notification.useNotification()
  const [errorMessage, setErrorMessage] = useState(null)
  const [gasProject, setGasProject] = useState(null)
  const [projectLoading, setProjectLoading] = useState(false)
  const { id } = useParams()

  const openNotification = (type, placement, message) => {
    api[type]({
      message,
      placement,
    })
  }

  const firstLoad = async () => {
    if (!id) return
    
    setProjectLoading(true)
    try {
      const searchQuery = {
        id,
        page: 1,
        pageSize: 1,
        fileNumber: 0,
        projectLevelEnum: 100,
        isStop: false,
      }
      const response = await getGasProjectsFullFilterApi(searchQuery)
      if (response?.data?.length > 0) {
        setGasProject(response.data[0])
      }
    } catch (error) {
      openNotification("error", "top", "خطا در دریافت اطلاعات پرونده")
    } finally {
      setProjectLoading(false)
    }
  }

  useEffect(() => {
    if (props.success) {
      openNotification("success", "top", props.success)
    }
    if (props.error) {
      openNotification("error", "top", props.error)
    }
    if (errorMessage) {
      openNotification("warning", "top", errorMessage)
    }
    onResetGasProjectFlag()
  }, [errorMessage, props.success, props.error])

  const [activeKey, setActiveKey] = useState("1")

  // Breadcrumb configuration
  const breadcrumbItems = [
    { title: <Link to="/dashboard">داشبورد</Link> },
    { title: <Link to="/projects/GasProjectExecutor">لیست پرونده</Link> },
    {
      title: gasProject ? (
        <Space>
          <Text strong style={{ fontFamily: "tahoma" }}>
            {gasProject.fileNumber}
          </Text>
          <Text type="secondary">|</Text>
          <Text>{gasProject.landlordName}</Text>
        </Space>
      ) : (
        "در حال بارگذاری..."
      ),
    },
  ]

  // Project Info Card
  const ProjectInfoCard = () => {
    if (!gasProject) return null

    return (
      <Card
        size="small"
        style={{
          marginBottom: 16,
          background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)",
          borderRadius: 12,
        }}
      >
        <Row gutter={[16, 12]}>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" size={2}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                <UserOutlined style={{ marginLeft: 4 }} />
                مالک
              </Text>
              <Text strong>{gasProject.landlordName || "-"}</Text>
            </Space>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Space direction="vertical" size={2}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                <HomeOutlined style={{ marginLeft: 4 }} />
                طبقه/واحد
              </Text>
              <Text strong>
                {gasProject.floorNumber === 0
                  ? gasProject.unitNumber > 1
                    ? "چند واحدی"
                    : "همکف"
                  : `طبقه ${gasProject.floorNumber}`}{" "}
                / {gasProject.unitNumber} واحد
              </Text>
            </Space>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Space direction="vertical" size={2}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                وضعیت
              </Text>
              <Tag color={gasProject.isCancel ? "error" : "success"}>
                {gasProject.projectLevel}
              </Tag>
            </Space>
          </Col>
          <Col xs={24} sm={12} md={10}>
            <Space direction="vertical" size={2}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                <EnvironmentOutlined style={{ marginLeft: 4 }} />
                آدرس
              </Text>
              <Text style={{ fontSize: 12 }}>{gasProject.address || "-"}</Text>
            </Space>
          </Col>
        </Row>
      </Card>
    )
  }

  // Tabs configuration
  const tabItems = gasProject => [
    {
      key: "1",
      label: (
        <span>
          <FileOutlined />
          فایلها
        </span>
      ),
      children: (
        <ProjectFiles
          rowData={gasProject}
          openNotification={openNotification}
          mainProps={props}
        />
      ),
    },
    {
      key: "2",
      label: (
        <span>
          <ToolOutlined />
          پکیج‌ها و نصاب
        </span>
      ),
      children: (
        <ProjectPackages
          rowData={gasProject}
          openNotification={openNotification}
          mainProps={props}
        />
      ),
    },
    {
      key: "3",
      label: (
        <span>
          <EditOutlined />
          ویرایش پرونده
        </span>
      ),
      children: (
        <ProjectUpdate
          rowData={gasProject}
          openNotification={openNotification}
          mainProps={props}
        />
      ),
    },
  ]

  useEffect(() => {
    firstLoad()
  }, [id, triggerReload])

  const handleTabChange = key => {
    setActiveKey(key)
  }

  return (
    <div className="page-content">
      <MetaTags>
        <title>پرونده | واحد گاز</title>
      </MetaTags>

      {/* Breadcrumb */}
      <Breadcrumb
        separator=">"
        style={{ margin: "16px 0", fontSize: 14 }}
        items={breadcrumbItems}
      />

      {contextHolder}

      {/* Loading Spinner */}
      <Spin
        spinning={projectLoading && !gasProject}
        tip="در حال بارگذاری..."
        size="large"
      >
        <div style={{ minHeight: 200 }}>
          {!projectLoading && !gasProject ? (
            <Card>
              <div style={{ textAlign: "center", padding: 40 }}>
                اطلاعات پرونده یافت نشد.
              </div>
            </Card>
          ) : (
            gasProject && (
              <>
                {/* Project Info Header */}
                <ProjectInfoCard />

                {/* Tabs */}
                <Card bodyStyle={{ padding: 16 }}>
                  <Tabs
                    activeKey={activeKey}
                    onChange={handleTabChange}
                    items={tabItems(gasProject)}
                    type="card"
                    destroyInactiveTabPane
                  />
                </Card>
              </>
            )
          )}
        </div>
      </Spin>
    </div>
  )
}

const mapStateToProps = ({ GasProjects }) => ({
  error: GasProjects.error,
  success: GasProjects.success,
  loading: GasProjects.loading,
  triggerReload: GasProjects.triggerReload,
})

const mapDispatchToProps = dispatch => ({
  onResetGasProjectFlag: () => dispatch(resetGasProjectFlag()),
  onAddFileGasProject: attachData => dispatch(addFileGasProject(attachData)),
  onAddFileGasProjectSingle: attachData =>
    dispatch(addFileGasProjectSingle(attachData)),
  // ProjectUpdate
  onUpdateGasProject: gasProject => dispatch(updateGasProject(gasProject)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(GasProjectDashboardExe))
